import React, { useCallback, useMemo, useState } from "react";
import {
  FileDown,
  FileSpreadsheet,
  Loader2,
  Pizza,
  RotateCw,
  Store,
} from "lucide-react";
import { toast } from "react-toastify";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";

import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import { lojaService } from "../../../services/lojaService";
import {
  fetchRelatorioSalaoPizzas,
  type RelatorioSalaoPizzasSecao,
} from "../../../services/pedidoIntegracaoService";
import {
  exportRelatorioSalaoPizzasPdf,
  exportRelatorioSalaoPizzasXlsx,
} from "../utils/relatorioSalaoPizzasExport";

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH = getDefaultMonthRange();

function formatQty(n: number): string {
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function tipoLabel(tipo: string | null): string {
  if (tipo === "doce") return "Doce";
  if (tipo === "salgada") return "Salgada";
  return "—";
}

function SecaoRelatorio({
  titulo,
  descricao,
  icon,
  secao,
}: {
  titulo: string;
  descricao: string;
  icon: React.ReactNode;
  secao: RelatorioSalaoPizzasSecao | undefined;
}) {
  const porTamanho = secao?.porTamanho ?? [];
  const linhas = secao?.linhas ?? [];
  const total = secao?.totalPizzas ?? 0;

  return (
    <section className="space-y-6 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex items-start gap-3 border-b border-neutral-100 pb-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-neutral-900">{titulo}</h2>
          <p className="mt-0.5 text-sm text-neutral-600">{descricao}</p>
          <p className="mt-2 text-sm text-neutral-700">
            Total:{" "}
            <span className="font-semibold tabular-nums text-neutral-900">
              {formatQty(total)}
            </span>
          </p>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-neutral-800">
          Por tamanho
        </h3>
        {porTamanho.length === 0 ? (
          <div className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
            Nenhuma pizza nesta seção no período.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-neutral-200">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-neutral-800">
                    Tamanho
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-neutral-800">
                    Qtd. pizzas
                  </th>
                </tr>
              </thead>
              <tbody>
                {porTamanho.map((row) => (
                  <tr
                    key={row.tamanhoNome}
                    className="border-b border-neutral-100 last:border-0"
                  >
                    <td className="px-4 py-2.5 text-neutral-900">
                      {row.tamanhoNome}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-neutral-900">
                      {formatQty(row.quantidadePizzas)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                <tr>
                  <td className="px-4 py-2.5 text-sm font-semibold text-neutral-800">
                    Total
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900">
                    {formatQty(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-neutral-800">
          Por sabor e tamanho
        </h3>
        {linhas.length === 0 ? (
          <div className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
            Nenhum sabor encontrado nesta seção.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-neutral-200">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-neutral-800">
                    Sabor
                  </th>
                  <th className="px-4 py-3 font-semibold text-neutral-800">
                    Tamanho
                  </th>
                  <th className="px-4 py-3 font-semibold text-neutral-800">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-neutral-800">
                    Qtd. pizzas
                  </th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((row) => (
                  <tr
                    key={`${row.saborNome}|${row.tamanhoNome}|${row.tipo ?? ""}`}
                    className="border-b border-neutral-100 last:border-0"
                  >
                    <td className="px-4 py-2.5 text-neutral-900">
                      {row.saborNome}
                    </td>
                    <td className="px-4 py-2.5 text-neutral-900">
                      {row.tamanhoNome}
                    </td>
                    <td className="px-4 py-2.5 text-neutral-700">
                      {tipoLabel(row.tipo)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-neutral-900">
                      {formatQty(row.quantidadePizzas)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-2.5 text-sm font-semibold text-neutral-800"
                  >
                    Total
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900">
                    {formatQty(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

const SalaoPizzas: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
  }>(null);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data, isFetching, refetch } = useQuery({
    queryKey: [
      "relatorio-salao-pizzas",
      applied?.dataInicio ?? "no-applied",
      applied?.dataFim ?? "no-applied",
      applied?.lojaId ?? "no-applied",
    ],
    enabled: applied != null,
    queryFn: () =>
      fetchRelatorioSalaoPizzas({
        dataInicio: applied!.dataInicio,
        dataFim: applied!.dataFim,
        lojaId: applied!.lojaId,
      }),
  });

  const handleAplicar = () => {
    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
    });
  };

  const pIni =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataInicio), false)
      : null;
  const pFim =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataFim), false)
      : null;
  const lojaLabel =
    applied != null
      ? applied.lojaId != null
        ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ?? "—")
        : "Todas"
      : null;

  const podeExportar =
    applied != null && !isFetching && (data?.totalPizzas ?? 0) > 0;

  const buildExportInput = useCallback(() => {
    if (!applied || !data) return null;
    return {
      periodoInicioISO: applied.dataInicio,
      periodoFimISO: applied.dataFim,
      filtros: [{ label: "Loja", value: lojaLabel ?? "—" }],
      secoes: [
        {
          titulo: "Salão (Foodfly)",
          totalPizzas: data.salao.totalPizzas,
          porTamanho: data.salao.porTamanho,
          linhas: data.salao.linhas,
        },
        {
          titulo: "Rodízio",
          totalPizzas: data.rodizio.totalPizzas,
          porTamanho: data.rodizio.porTamanho,
          linhas: data.rodizio.linhas,
        },
      ],
      totalPizzas: data.totalPizzas,
      geradoEmLabel: BrazilDate.formatBR(new Date(), true),
    };
  }, [applied, data, lojaLabel]);

  const handleExportExcel = useCallback(() => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    if ((data?.totalPizzas ?? 0) === 0) {
      toast.warning("Não há linhas para exportar.");
      return;
    }
    try {
      const input = buildExportInput();
      if (!input) {
        toast.warning("Aplique os filtros antes de exportar.");
        return;
      }
      exportRelatorioSalaoPizzasXlsx(input);
      toast.success("Planilha Excel baixada.");
    } catch {
      toast.error("Não foi possível gerar o Excel.");
    }
  }, [applied, data?.totalPizzas, buildExportInput]);

  const handleExportPdf = useCallback(() => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    if ((data?.totalPizzas ?? 0) === 0) {
      toast.warning("Não há linhas para exportar.");
      return;
    }
    try {
      const input = buildExportInput();
      if (!input) {
        toast.warning("Aplique os filtros antes de exportar.");
        return;
      }
      exportRelatorioSalaoPizzasPdf(input);
      toast.success("PDF baixado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  }, [applied, data?.totalPizzas, buildExportInput]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Pizzas Salão"
        description=""
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={!podeExportar}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition-opacity hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileSpreadsheet size={18} />
              Excel
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={!podeExportar}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileDown size={18} />
              PDF (A4)
            </button>
          </div>
        }
      />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="salao-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Data início
            </label>
            <input
              id="salao-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="salao-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Data fim
            </label>
            <input
              id="salao-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              inputId="salao-loja"
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma loja"}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied != null ? (
        <p className="mb-4 text-sm text-neutral-600">
          Período: <span className="font-medium text-neutral-800">{pIni}</span>{" "}
          a <span className="font-medium text-neutral-800">{pFim}</span>
          {" · "}
          Loja:{" "}
          <span className="font-medium text-neutral-800">{lojaLabel}</span>
          {" · "}
          Total (salão + rodízio):{" "}
          <span className="font-medium text-neutral-800">
            {formatQty(data?.totalPizzas ?? 0)}
          </span>
          {" · "}
          Salão:{" "}
          <span className="font-medium text-neutral-800">
            {formatQty(data?.salao.totalPizzas ?? 0)}
          </span>
          {" · "}
          Rodízio:{" "}
          <span className="font-medium text-neutral-800">
            {formatQty(data?.rodizio.totalPizzas ?? 0)}
          </span>
        </p>
      ) : (
        <p className="mb-4 text-sm text-neutral-600">
          Selecione o período e clique em <strong>Aplicar filtros</strong>.
        </p>
      )}

      <div className="relative">
        {applied != null && isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {applied != null ? (
          <div className="space-y-8">
            <SecaoRelatorio
              titulo="Salão (Foodfly)"
              descricao="Pizzas atendidas no salão e venda balcão — mesma base do «Qtd. Salão» do dashboard."
              icon={<Store size={22} strokeWidth={2} />}
              secao={data?.salao}
            />
            <SecaoRelatorio
              titulo="Rodízio"
              descricao="Pizzas do rodízio finalizadas — mesma base do «Qtd. Rodízio» do dashboard."
              icon={<RotateCw size={22} strokeWidth={2} />}
              secao={data?.rodizio}
            />

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
              <span className="inline-flex items-center gap-2 font-medium text-neutral-900">
                <Pizza size={16} aria-hidden />
                Total geral
              </span>
              <span className="ml-2 tabular-nums font-bold text-neutral-900">
                {formatQty(data?.totalPizzas ?? 0)}
              </span>
              <span className="ml-2 text-neutral-500">
                (salão {formatQty(data?.salao.totalPizzas ?? 0)} + rodízio{" "}
                {formatQty(data?.rodizio.totalPizzas ?? 0)})
              </span>
            </div>
          </div>
        ) : null}

        {applied != null ? (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            Recarregar dados
          </button>
        ) : null}
      </div>
    </main>
  );
};

export default SalaoPizzas;
